import type { ArtifactType } from '@/config/objects/artifacts';
import { HandCoins, Shield, Snowflake, Sword, Zap } from 'lucide-react';

/**
 * Per-type overlays and accents the demo layers onto the mock hunt screen
 * while an effect is "active". Pure CSS/Tailwind; every animation carries a
 * motion-reduce fallback that keeps the end state visible without movement.
 */

/** Keyframes shared by the vignettes. Scoped names to avoid global clashes. */
export function VignetteKeyframes() {
  return (
    <style>{`
      @keyframes artifact-demo-jitter {
        0%, 100% { transform: translate(0, 0); }
        25% { transform: translate(1px, -1px); }
        50% { transform: translate(-1px, 1px); }
        75% { transform: translate(1px, 1px); }
      }
      @keyframes artifact-demo-float-up {
        0% { opacity: 0; transform: translateY(6px); }
        20% { opacity: 1; }
        100% { opacity: 0; transform: translateY(-26px); }
      }
      @keyframes artifact-demo-fly-in-bounce {
        0% { opacity: 0; transform: translate(70px, -70px) rotate(45deg); }
        55% { opacity: 1; transform: translate(0, 0) rotate(0deg); }
        70% { transform: translate(16px, -16px) rotate(-15deg); }
        100% { opacity: 0; transform: translate(56px, -56px) rotate(-30deg); }
      }
      @keyframes artifact-demo-pin-drift {
        0% { transform: translate(0, 0); }
        100% { transform: translate(64px, 38px); }
      }
      @keyframes artifact-demo-needle {
        0% { transform: rotate(140deg); }
        60% { transform: rotate(28deg); }
        75% { transform: rotate(42deg); }
        100% { transform: rotate(35deg); }
      }
      @keyframes artifact-demo-scan {
        0% { transform: translateY(0); }
        100% { transform: translateY(72px); }
      }
      @keyframes artifact-demo-drain {
        from { width: 100%; }
        to { width: 0%; }
      }
      @keyframes artifact-demo-box-wobble {
        0%, 100% { transform: rotate(0deg) scale(1); }
        20% { transform: rotate(-8deg) scale(1.04); }
        40% { transform: rotate(8deg) scale(1.06); }
        60% { transform: rotate(-6deg) scale(1.08); }
        80% { transform: rotate(6deg) scale(1.1); }
      }
      @media (prefers-reduced-motion: reduce) {
        .artifact-demo-animated { animation: none !important; }
      }
    `}</style>
  );
}

const overlayBase =
  'pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 text-center';

function ScrambleVignette() {
  return (
    <div className={`${overlayBase} bg-red-950/30`}>
      <p
        className="artifact-demo-animated text-lg font-semibold tracking-widest text-red-200 blur-[1px]"
        style={{ animation: 'artifact-demo-jitter 0.15s infinite' }}
      >
        M#kε Wαy f0r Dμckl!ngs
      </p>
      <p className="text-[11px] text-red-200/80">Text scrambled</p>
    </div>
  );
}

function FreezeVignette() {
  return (
    <div className={`${overlayBase} bg-gradient-to-b from-sky-200/40 via-sky-100/25 to-white/30 backdrop-blur-[2px]`}>
      <Snowflake className="h-9 w-9 text-sky-100 drop-shadow motion-safe:animate-spin [animation-duration:6s]" />
      <p className="text-xs font-semibold text-sky-50 drop-shadow">Frozen — controls locked</p>
    </div>
  );
}

function BlackoutVignette() {
  return (
    <div className={`${overlayBase} bg-black/90 transition-opacity duration-700`}>
      <p className="text-xs font-medium text-zinc-400">Map blacked out</p>
    </div>
  );
}

function StealVignette({ points }: { points: number }) {
  return (
    <div className={`${overlayBase} justify-start pt-12`}>
      <span
        className="artifact-demo-animated flex items-center gap-1 rounded-full bg-red-500/90 px-2.5 py-1 text-xs font-bold text-white"
        style={{ animation: 'artifact-demo-float-up 2.2s ease-out infinite' }}
      >
        <HandCoins className="h-3.5 w-3.5" /> −{points} pts
      </span>
    </div>
  );
}

function MisdirectVignette() {
  return (
    <div className={`${overlayBase} justify-start`}>
      <div className="relative mt-16 h-24 w-full">
        <span
          className="artifact-demo-animated absolute left-1/3 top-2 text-amber-300"
          style={{ animation: 'artifact-demo-pin-drift 2.4s ease-in-out infinite alternate' }}
        >
          <svg width="18" height="24" viewBox="0 0 18 24" fill="currentColor" aria-hidden>
            <path d="M9 0C4 0 0 4 0 9c0 6.6 9 15 9 15s9-8.4 9-15c0-5-4-9-9-9zm0 12.5A3.5 3.5 0 1 1 9 5.5a3.5 3.5 0 0 1 0 7z" />
          </svg>
        </span>
        <p className="absolute bottom-0 w-full text-[11px] text-amber-200/90">Pins drifting off course</p>
      </div>
    </div>
  );
}

function DoubleDownVignette() {
  return (
    <div className={`${overlayBase} justify-start pt-10`}>
      <span className="flex items-center gap-1 rounded-full bg-purple-500 px-3 py-1 text-sm font-bold text-white shadow-lg motion-safe:animate-pulse">
        <Zap className="h-4 w-4" /> ×2 points active
      </span>
      <p className="text-[11px] text-purple-200/90">250 pts → 500 pts</p>
    </div>
  );
}

function ShieldVignette() {
  return (
    <div className={overlayBase}>
      <div className="absolute inset-2 rounded-[1.6rem] border-2 border-blue-400/70 motion-safe:animate-pulse" />
      <span
        className="artifact-demo-animated absolute right-8 top-16 text-red-400"
        style={{ animation: 'artifact-demo-fly-in-bounce 2.6s ease-in-out infinite' }}
      >
        <Sword className="h-6 w-6" />
      </span>
      <span className="flex items-center gap-1.5 rounded-full bg-blue-500/90 px-3 py-1 text-xs font-semibold text-white">
        <Shield className="h-3.5 w-3.5" /> Attack blocked
      </span>
    </div>
  );
}

export interface VignetteProps {
  type: ArtifactType;
  /** Point figure used by steal copy (from effectValue). */
  points: number;
}

export function EffectVignette({ type, points }: VignetteProps) {
  switch (type) {
    case 'scramble':
      return <ScrambleVignette />;
    case 'freeze':
      return <FreezeVignette />;
    case 'blackout':
      return <BlackoutVignette />;
    case 'steal':
      return <StealVignette points={points} />;
    case 'misdirect':
      return <MisdirectVignette />;
    case 'double_down':
      return <DoubleDownVignette />;
    case 'shield':
      return <ShieldVignette />;
    default: {
      const exhaustive: never = type;
      return exhaustive;
    }
  }
}
